import { openDB, type DBSchema } from 'idb';

export type OfflineOperationType = 'MAINTENANCE_RECORD_CREATE' | 'ISSUE_CREATE';

export interface OfflineOperation {
  id: string;
  type: OfflineOperationType;
  payload: Record<string, unknown>;
  createdAt: string;
  attempts: number;
  lastError?: string;
  ownerUserId?: string;
}

interface MrWashDb extends DBSchema {
  queue: {
    key: string;
    value: OfflineOperation;
    indexes: { 'by-created': string };
  };
  settings: {
    key: string;
    value: { key: string; value: string };
  };
}

const dbPromise = openDB<MrWashDb>('mrwash-technik', 1, {
  upgrade(db) {
    const queue = db.createObjectStore('queue', { keyPath: 'id' });
    queue.createIndex('by-created', 'createdAt');
    db.createObjectStore('settings', { keyPath: 'key' });
  }
});

export async function enqueue(type: OfflineOperationType, payload: Record<string, unknown>, ownerUserId: string): Promise<OfflineOperation> {
  const operation: OfflineOperation = {
    id: crypto.randomUUID(),
    type,
    payload,
    createdAt: new Date().toISOString(),
    attempts: 0,
    ownerUserId
  };
  await (await dbPromise).put('queue', operation);
  window.dispatchEvent(new CustomEvent('mrwash-queue-changed'));
  return operation;
}

export async function listQueued(ownerUserId?: string): Promise<OfflineOperation[]> {
  const operations = await (await dbPromise).getAllFromIndex('queue', 'by-created');
  return ownerUserId ? operations.filter((operation) => operation.ownerUserId === ownerUserId) : operations;
}

export async function removeQueued(id: string): Promise<void> {
  await (await dbPromise).delete('queue', id);
  window.dispatchEvent(new CustomEvent('mrwash-queue-changed'));
}

export async function markQueueError(id: string, message: string): Promise<void> {
  const db = await dbPromise;
  const existing = await db.get('queue', id);
  if (existing) await db.put('queue', { ...existing, attempts: existing.attempts + 1, lastError: message });
  window.dispatchEvent(new CustomEvent('mrwash-queue-changed'));
}

export async function queuedCount(ownerUserId?: string): Promise<number> {
  if (!ownerUserId) return (await dbPromise).count('queue');
  return (await listQueued(ownerUserId)).length;
}

export async function getSetting(key: string): Promise<string | undefined> {
  return (await dbPromise).get('settings', key).then((result) => result?.value);
}

export async function setSetting(key: string, value: string): Promise<void> {
  await (await dbPromise).put('settings', { key, value });
}

export async function getDeviceName(): Promise<string> {
  const existing = await getSetting('deviceName');
  if (existing) return existing;
  const platform = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform || navigator.platform || 'Gerät';
  const created = `${platform}-${crypto.randomUUID().slice(0, 8)}`;
  await setSetting('deviceName', created);
  return created;
}
